<template>
	<div id="meeting-detail" class="meeting-panel">
		<header class="meeting-panel-header">
			<button
				v-if="view !== 'MAIN'"
				type="button"
				class="icon-action"
				:aria-label="scheduledMeeting.meetingName"
				:title="scheduledMeeting.meetingName"
				@click="view = 'MAIN'"
			>
				<portal-icon name="arrow-left" :size="18"></portal-icon>
			</button>
			<div class="meeting-title">
				<portal-status-pill
					:background="isInactive ? 'var(--v-alert_10-base)' : 'var(--v-success_10-base)'"
					:color="isInactive ? 'var(--v-alert-base)' : 'var(--v-success-base)'"
				>
					{{ scheduledMeeting.meetingStatus === 'NoShow' ? $t('meetings.filter.no-show') : isCancelled ? $t('meetings.detail.cancelled') : $t('meetings.filter.scheduled') }}
				</portal-status-pill>
				<h1>{{ scheduledMeeting.meetingName }}</h1>
			</div>
			<button
				type="button"
				class="icon-action close-action"
				:aria-label="$t('global.close')"
				:title="$t('global.close')"
				@click="$emit('result')"
			>
				<portal-icon name="x" :size="19"></portal-icon>
			</button>
		</header>

		<div v-if="!isReady" class="meeting-panel-loading">
			<v-progress-circular indeterminate :size="36" :width="3" color="primary"></v-progress-circular>
		</div>

		<main v-else-if="view === 'MAIN'" class="meeting-panel-body show-scrollbar">
			<section class="schedule-card" :class="{ cancelled: isCancelled }">
				<span class="schedule-icon">
					<portal-icon name="calendar-days" :size="21"></portal-icon>
				</span>
				<div>
					<span>{{ $t('meetings.detail.time') }}</span>
					<strong :class="{ strike: isCancelled }">{{ formattedStart }}</strong>
					<small>{{ durationMinutes }} {{ $t('meetings.detail.minutes') }}</small>
				</div>
			</section>

			<section class="panel-section">
				<h2>{{ $t('meetings.booked.booked-by') }}</h2>
				<div class="detail-list">
					<div class="detail-row">
						<span class="detail-icon"><portal-icon name="user-round" :size="17"></portal-icon></span>
						<div>
							<label>{{ $t('meetings.detail.name') }}</label>
							<strong>{{ contactName }}</strong>
						</div>
					</div>
					<a class="detail-row" :href="`mailto:${scheduledMeeting.formData.email}`">
						<span class="detail-icon"><portal-icon name="mail" :size="17"></portal-icon></span>
						<div>
							<label>{{ $t('meetings.detail.email') }}</label>
							<strong>{{ scheduledMeeting.formData.email }}</strong>
						</div>
					</a>
					<a
						v-if="scheduledMeeting.formData.phone"
						class="detail-row"
						:href="`tel:${scheduledMeeting.formData.phone}`"
					>
						<span class="detail-icon"><portal-icon name="phone" :size="17"></portal-icon></span>
						<div>
							<label>{{ $t('meetings.detail.phone') }}</label>
							<strong>{{ scheduledMeeting.formData.phone }}</strong>
						</div>
					</a>
				</div>
			</section>

			<section class="panel-section" v-if="locationType">
				<h2>{{ locationHeading }}</h2>
				<div class="location-card">
					<span class="detail-icon location-icon">
						<portal-icon :name="locationIcon" :size="18"></portal-icon>
					</span>
					<div class="location-copy">
						<template v-if="locationType === 'Zoom' && scheduledMeeting.zoomMeeting">
							<a :href="scheduledMeeting.zoomMeeting.join_url" target="_blank" rel="noopener">
								{{ $t('meetings.detail.join') }}
								<portal-icon name="external-link" :size="14"></portal-icon>
							</a>
							<span>{{ scheduledMeeting.zoomMeeting.join_url }}</span>
							<small>{{ $t('meetings.detail.id') }} {{ scheduledMeeting.zoomMeeting.id }}</small>
							<small v-if="scheduledMeeting.zoomMeeting.pstnPassword">
								{{ $t('meetings.detail.passcode') }} {{ scheduledMeeting.zoomMeeting.pstnPassword }}
							</small>
							<small v-if="zoomDialIn">
								{{ $t('meetings.detail.dial-in') }} {{ normalizedZoomDialIn }}
							</small>
						</template>
						<template v-else-if="locationType === 'Google' && scheduledMeeting.googleMeeting">
							<a :href="scheduledMeeting.googleMeeting.hangoutLink" target="_blank" rel="noopener">
								{{ $t('meetings.detail.join') }}
								<portal-icon name="external-link" :size="14"></portal-icon>
							</a>
							<span>{{ scheduledMeeting.googleMeeting.hangoutLink }}</span>
							<small v-if="googleDialIn">
								{{ $t('meetings.detail.dial-in') }} {{ googleDialIn.label }} ({{ googleDialIn.regionCode }})
							</small>
							<small v-if="googleDialIn">{{ $t('meetings.detail.pin') }} {{ googleDialIn.pin }}</small>
						</template>
						<strong v-else>{{ locationDescription }}</strong>
					</div>
				</div>
			</section>

			<section class="panel-section answer-section" v-if="hasAnswers">
				<div v-for="(answer, index) in scheduledMeeting.formData.answers" :key="index" class="answer-card">
					<label>{{ questionText(answer.question) }}</label>
					<p>{{ answer.answer }}</p>
				</div>
			</section>
		</main>

		<div v-else class="meeting-frame">
			<i-frame-wrapper
				:frame-url="view === 'CANCEL' ? cancelUrl : rescheduleUrl"
				@frame-event="frameEvent"
			></i-frame-wrapper>
		</div>

		<footer class="meeting-panel-footer" v-if="isReady && view === 'MAIN' && !isInactive">
			<button type="button" class="cancel-action" @click="view = 'CANCEL'">
				{{ $t('meetings.detail.cancel') }}
			</button>
			<button type="button" class="reschedule-action" @click="view = 'RESCHEDULE'">
				<portal-icon name="calendar-days" :size="16"></portal-icon>
				{{ $t('meetings.detail.reschedule') }}
			</button>
		</footer>
	</div>
</template>

<script>
	import MeetingService from '@/modules/meetings/MeetingService';
	import { DateTime } from 'luxon';
	import IFrameWrapper from '@/modules/meetings/iFrameWrapper';
	import { questionText } from '@/modules/forms/v2/schema/questionText';

	export default {
		name: 'MeetingDetail',

		props: ['scheduledMeeting'],

		components: { IFrameWrapper },

		data() {
			return {
				view: 'MAIN',
				meetingService: new MeetingService(),
				baseUrl: process.env.VUE_APP_MEET_LINK,
				meeting: null,
				isReady: false,
			};
		},

		mounted() {
			this.getMeeting();
		},

		methods: {
			questionText,
			frameEvent(event) {
				if (event.data === 'MEETING_CANCELLED' || event.data === 'MEETING_RESCHEDULED') {
					this.$emit('result', true);
				}
			},

			getMeeting() {
				this.meetingService.getMeeting(this.scheduledMeeting.meetingId).then((res) => {
					this.meeting = res.data;
					this.isReady = true;
				});
			},
		},

		computed: {
			fullMeetingLink() {
				return this.meeting.meetingLink;
			},

			cancelUrl() {
				return `${this.fullMeetingLink}/?id=${this.scheduledMeeting.id}&action=cancel&inFrame=true`;
			},

			rescheduleUrl() {
				return `${this.fullMeetingLink}/?id=${this.scheduledMeeting.id}&action=change&inFrame=true`;
			},

			isInactive() {
				return this.scheduledMeeting && this.scheduledMeeting.meetingStatus !== 'Scheduled';
			},

			isCancelled() {
				return this.scheduledMeeting && this.scheduledMeeting.meetingStatus === 'Cancelled';
			},

			hasAnswers() {
				return Boolean(
					this.scheduledMeeting.formData &&
						this.scheduledMeeting.formData.answers &&
						this.scheduledMeeting.formData.answers.length
				);
			},

			contactName() {
				return [this.scheduledMeeting.formData.firstName, this.scheduledMeeting.formData.lastName]
					.filter(Boolean)
					.join(' ');
			},

			formattedStart() {
				return DateTime.fromISO(this.scheduledMeeting.confirmedTime.start).toFormat("cccc, LLL dd yyyy '@' t");
			},

			durationMinutes() {
				return DateTime.fromISO(this.scheduledMeeting.confirmedTime.end).diff(
					DateTime.fromISO(this.scheduledMeeting.confirmedTime.start),
					'minutes'
				).minutes;
			},

			locationType() {
				return this.scheduledMeeting.location && this.scheduledMeeting.location.type;
			},

			locationHeading() {
				if (this.locationType === 'Zoom' || this.locationType === 'Google') {
					return this.$t('meetings.detail.join');
				}
				if (this.locationType === 'InPerson') return this.$t('meetings.detail.meet');
				if (this.locationType === 'PhoneOut') return this.$t('meetings.detail.call-invitee');
				return this.$t('meetings.detail.invitee-call');
			},

			locationIcon() {
				if (this.locationType === 'Zoom' || this.locationType === 'Google') return 'video';
				if (this.locationType === 'InPerson') return 'map-pin';
				return 'phone';
			},

			locationDescription() {
				const location = this.scheduledMeeting.location || {};
				if (this.locationType === 'PhoneIn') return location.phone;
				if (this.locationType === 'PhoneOut') return this.scheduledMeeting.formData.phone;
				if (this.locationType === 'InPerson') return location.address;
				return location.custom;
			},

			zoomDialIn() {
				const settings = this.scheduledMeeting.zoomMeeting && this.scheduledMeeting.zoomMeeting.settings;
				const numbers = settings && settings.global_dial_in_numbers;
				return numbers && numbers.length ? numbers[0] : null;
			},

			normalizedZoomDialIn() {
				if (!this.zoomDialIn) return '';
				return `${this.zoomDialIn.number.replaceAll(' ', '')} (${this.zoomDialIn.city}, ${
					this.zoomDialIn.country_name
				})`;
			},

			googleDialIn() {
				const entryPoints =
					this.scheduledMeeting.googleMeeting && this.scheduledMeeting.googleMeeting.entryPoints;
				return entryPoints ? entryPoints.find((entry) => entry.entryPointType === 'phone') : null;
			},
		},
	};
</script>

<style scoped lang="scss">
	.meeting-panel {
		height: 100%;
		display: flex;
		flex-direction: column;
		background: var(--portal-surface);
		color: var(--portal-text);
		text-align: left;
	}

	.meeting-panel-header {
		min-height: 78px;
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 18px 18px 14px;
		border-bottom: 1px solid var(--portal-border);
	}

	.meeting-title {
		min-width: 0;
		flex: 1;
		text-align: left;
	}

	.meeting-title h1 {
		margin: 5px 0 0;
		font-size: 19px;
		font-weight: 650;
		line-height: 1.3;
	}

	.icon-action {
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		display: grid;
		place-items: center;
		padding: 0;
		border: 0;
		border-radius: var(--brand-button-radius, 10px);
		background: transparent;
		color: var(--portal-muted);
		cursor: pointer;
	}

	.icon-action:hover,
	.icon-action:focus-visible {
		outline: 0;
		background: var(--v-gray_20-base);
		color: var(--portal-text);
	}

	.close-action {
		margin-left: auto;
	}

	.meeting-panel-loading {
		flex: 1;
		display: grid;
		place-items: center;
	}

	.meeting-panel-body {
		min-height: 0;
		flex: 1;
		padding: 18px;
		overflow-y: auto;
	}

	.schedule-card {
		display: flex;
		align-items: center;
		gap: 13px;
		padding: 15px;
		border: 1px solid color-mix(in srgb, var(--v-primary-base) 18%, var(--portal-border));
		border-radius: var(--brand-surface-radius, 15px);
		background: color-mix(in srgb, var(--v-primary-base) 6%, var(--portal-surface));
	}

	.schedule-card.cancelled {
		border-color: var(--portal-border);
		background: var(--v-gray_20-base);
	}

	.schedule-icon {
		width: 42px;
		height: 42px;
		flex: 0 0 42px;
		display: grid;
		place-items: center;
		border-radius: 13px;
		background: var(--portal-surface);
		color: var(--v-primary-base);
	}

	.schedule-card > div {
		min-width: 0;
		flex: 1;
		display: grid;
		gap: 2px;
		text-align: left;
	}

	.schedule-card span,
	.schedule-card small {
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 600;
	}

	.schedule-card strong {
		font-size: 14px;
		font-weight: 650;
	}

	.strike {
		text-decoration: line-through;
	}

	.panel-section {
		margin-top: 24px;
	}

	.panel-section h2 {
		margin: 0 0 10px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 700;
		letter-spacing: 0.04em;
		text-align: left;
		text-transform: uppercase;
	}

	.detail-list {
		overflow: hidden;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 15px);
	}

	.detail-row {
		min-height: 62px;
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 10px 13px;
		border-bottom: 1px solid var(--portal-border);
		color: inherit;
		text-decoration: none;
	}

	.detail-row:last-child {
		border-bottom: 0;
	}

	a.detail-row:hover {
		background: var(--v-gray_20-base);
	}

	.detail-icon {
		width: 34px;
		height: 34px;
		flex: 0 0 34px;
		display: grid;
		place-items: center;
		border-radius: 10px;
		background: var(--v-gray_20-base);
		color: var(--portal-muted);
	}

	.detail-row > div {
		min-width: 0;
		flex: 1;
		display: grid;
		gap: 2px;
		text-align: left;
	}

	.detail-row label {
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 600;
	}

	.detail-row strong {
		overflow-wrap: anywhere;
		font-size: 14px;
		font-weight: 600;
	}

	.location-card {
		display: flex;
		align-items: flex-start;
		gap: 12px;
		padding: 13px;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 15px);
	}

	.location-icon {
		color: var(--v-primary-base);
	}

	.location-copy {
		min-width: 0;
		flex: 1;
		display: grid;
		gap: 4px;
		padding-top: 2px;
		text-align: left;
	}

	.location-copy a {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		color: var(--v-primary-base);
		font-size: 14px;
		font-weight: 650;
		text-decoration: none;
	}

	.location-copy span,
	.location-copy small {
		color: var(--portal-muted);
		font-size: 12px;
		overflow-wrap: anywhere;
	}

	.location-copy strong {
		font-size: 14px;
		font-weight: 600;
		overflow-wrap: anywhere;
	}

	.answer-section {
		display: grid;
		gap: 10px;
	}

	.answer-card {
		padding: 13px;
		border-radius: var(--brand-surface-radius, 13px);
		background: var(--v-gray_20-base);
		text-align: left;
	}

	.answer-card label {
		display: block;
		margin-bottom: 4px;
		color: var(--portal-muted);
		font-size: 12px;
		font-weight: 650;
	}

	.answer-card p {
		margin: 0;
		font-size: 14px;
		line-height: 1.5;
	}

	.meeting-frame {
		min-height: 0;
		flex: 1;
		overflow-y: auto;
	}

	.meeting-panel-footer {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		padding: 12px 64px 12px 18px;
		border-top: 1px solid var(--portal-border);
		background: var(--portal-surface);
	}

	.cancel-action,
	.reschedule-action {
		min-height: 38px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 7px;
		padding: 0 13px;
		border-radius: var(--brand-button-radius, 10px);
		font: inherit;
		font-size: 13px;
		font-weight: 650;
		cursor: pointer;
	}

	.cancel-action {
		border: 0;
		background: transparent;
		color: var(--v-alert-base);
	}

	.cancel-action:hover {
		background: var(--v-alert_10-base);
	}

	.reschedule-action {
		border: 1px solid var(--v-primary-base);
		background: var(--v-primary-base);
		color: var(--v-white-base);
	}

	@media (max-width: 599px) {
		.meeting-panel-header {
			padding: 15px 14px 12px;
		}

		.meeting-panel-body {
			padding: 14px;
		}

		.meeting-panel-footer {
			padding: 10px 64px 10px 14px;
		}
	}
</style>
